// Minimal, dependency-free ICU MessageFormat subset:
//   {name}                         simple argument
//   {count, plural, =0 {...} one {# item} other {# items}}   (Intl.PluralRules, '#' = formatted count)
//   {count, selectordinal, one {#st} two {#nd} few {#rd} other {#th}}
//   {gender, select, female {...} male {...} other {...}}
//   {amount, number} / {amount, number, currency} / {day, date} / {day, date, long}
//   '{' escaping with apostrophes: '' → '   '{literal}' → {literal}
// Messages are parsed once and cached. Parsing errors are reported, never thrown at render time.

export type Node =
  | { k: 'text'; v: string }
  | { k: 'arg'; name: string }
  | { k: 'hash' }
  | { k: 'fmt'; name: string; type: 'number' | 'date' | 'time'; style?: string }
  | { k: 'plural'; name: string; ordinal: boolean; offset: number; options: Record<string, Node[]> }
  | { k: 'select'; name: string; options: Record<string, Node[]> };

export class IcuSyntaxError extends Error {}

export function parse(src: string): Node[] {
  let i = 0;
  function parseMessage(inPlural: boolean, depth: number): Node[] {
    const out: Node[] = [];
    let text = '';
    const flush = () => { if (text) { out.push({ k: 'text', v: text }); text = ''; } };
    while (i < src.length) {
      const ch = src[i]!;
      if (ch === "'") {
        if (src[i + 1] === "'") { text += "'"; i += 2; continue; }
        if (src[i + 1] === '{' || src[i + 1] === '}' || (inPlural && src[i + 1] === '#')) {
          const end = src.indexOf("'", i + 1);
          if (end < 0) { text += src.slice(i + 1); i = src.length; continue; }
          text += src.slice(i + 1, end); i = end + 1; continue;
        }
        text += ch; i++; continue;
      }
      if (ch === '{') { flush(); i++; out.push(parseArg(depth)); continue; }
      if (ch === '}') { if (depth === 0) throw new IcuSyntaxError(`Unexpected } at ${i}`); break; }
      if (ch === '#' && inPlural) { flush(); out.push({ k: 'hash' }); i++; continue; }
      text += ch; i++;
    }
    flush();
    return out;
  }
  function ws() { while (i < src.length && /\s/.test(src[i]!)) i++; }
  function ident(): string {
    ws();
    const m = /^[^\s,{}]+/.exec(src.slice(i));
    if (!m) throw new IcuSyntaxError(`Expected identifier at ${i}`);
    i += m[0].length; ws();
    return m[0];
  }
  function expect(c: string) { ws(); if (src[i] !== c) throw new IcuSyntaxError(`Expected '${c}' at ${i} in "${src}"`); i++; }
  function parseOptions(inPlural: boolean, depth: number): Record<string, Node[]> {
    const options: Record<string, Node[]> = {};
    ws();
    while (i < src.length && src[i] !== '}') {
      const key = ident();
      expect('{');
      options[key] = parseMessage(inPlural, depth + 1);
      expect('}');
      ws();
    }
    if (!('other' in options)) throw new IcuSyntaxError(`Missing 'other' option in "${src}"`);
    return options;
  }
  function parseArg(depth: number): Node {
    const name = ident();
    if (src[i] === '}') { i++; return { k: 'arg', name }; }
    expect(',');
    const type = ident();
    if (type === 'plural' || type === 'selectordinal') {
      expect(',');
      ws();
      let offset = 0;
      const m = /^offset:(\d+)/.exec(src.slice(i));
      if (m) { offset = Number(m[1]); i += m[0].length; }
      const options = parseOptions(true, depth);
      expect('}');
      return { k: 'plural', name, ordinal: type === 'selectordinal', offset, options };
    }
    if (type === 'select') {
      expect(',');
      const options = parseOptions(false, depth);
      expect('}');
      return { k: 'select', name, options };
    }
    if (type === 'number' || type === 'date' || type === 'time') {
      let style: string | undefined;
      if (src[i] === ',') { i++; style = ident(); }
      expect('}');
      return { k: 'fmt', name, type, style };
    }
    throw new IcuSyntaxError(`Unknown argument type '${type}' in "${src}"`);
  }
  const res = parseMessage(false, 0);
  if (i < src.length) throw new IcuSyntaxError(`Unbalanced braces in "${src}"`);
  return res;
}

export interface FormatEnv {
  locale: string;
  currency: string;
  number(n: number, style?: string): string;
  date(d: string | Date, style?: string): string;
  time(d: string | Date, style?: string): string;
  plural(n: number, ordinal: boolean): string;
}

export type Params = Record<string, string | number | boolean | Date | null | undefined>;

export function render(nodes: Node[], params: Params, env: FormatEnv, hashValue?: number): string {
  let out = '';
  for (const n of nodes) {
    switch (n.k) {
      case 'text': out += n.v; break;
      case 'hash': out += hashValue === undefined ? '#' : env.number(hashValue); break;
      case 'arg': {
        const v = params[n.name];
        out += v === undefined || v === null ? `{${n.name}}` : typeof v === 'number' ? env.number(v) : v instanceof Date ? env.date(v) : String(v);
        break;
      }
      case 'fmt': {
        const v = params[n.name];
        if (v === undefined || v === null || v === '') { out += ''; break; }
        if (n.type === 'number') out += env.number(Number(v), n.style);
        else if (n.type === 'date') out += env.date(v instanceof Date ? v : String(v), n.style);
        else out += env.time(v instanceof Date ? v : String(v), n.style);
        break;
      }
      case 'plural': {
        const raw = Number(params[n.name] ?? 0);
        const val = raw - n.offset;
        const exact = n.options[`=${raw}`];
        const branch = exact ?? n.options[env.plural(val, n.ordinal)] ?? n.options.other!;
        out += render(branch, params, env, val);
        break;
      }
      case 'select': {
        const v = String(params[n.name] ?? 'other');
        out += render(n.options[v] ?? n.options.other!, params, env, hashValue);
        break;
      }
    }
  }
  return out;
}
